import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';
import { AccesOperationTLR } from '../entities/acces-operation-tlr.entity';
import { DemandeIntervention } from '../entities/demande-intervention.entity';

import { ArretSlbService } from '../../arret-slb/services/arret-slb.service';
import { ArretSlbTLR } from '../../arret-slb/entities/arret-slb-tlr.entity';

import { ActeOnMService } from '../../../actes-onm/services/actes-onm.service';
import { ActeOnMTLR } from '../../../actes-onm/entities/acte-onm-tlr.entity';

import { ConstructeurService } from '../../../constructeur/services/constructeur.service';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';
import { IntervenantAccesDTO } from '../entities/intervenant-acces-dto.entity';

import { ModeleOuvrageService } from '../../../structure/modele-ouvrage/services/modele-ouvrage.service';

import { MotifRefusService } from '../services/motif-refus.service';

import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { NiveauAlerteIncendie } from '../../../../static-types/entities/niveau-alerte-incendie.entity';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';
import { WorkOrder } from '../entities/work-order.entity';
import { Notification } from '../entities/notification.entity';

import { MemoService } from '../../../memo/services/memo.service';
import { Memo } from '../../../memo/entities/memo.entity';
import { TypeDocument } from '../../../humain/intervenant/components/intervenant-status.component';

import { applicationConstants, couleurIndicateurColors } from '../../../../app.constants';
import { LocalDateTime } from '../../../../shared/jsr310-localdates/types/local-date-time.type';

type DisplayedMemo = {
	entity: Memo,
	categorie: string,
}

class DisplayedParticipant {
	roleLabel!: string; 
	nomEntreprise!: string;
	documentApplicable!: string;
	nomIntervenant!: string | null;
	intervenant!: IntervenantAccesDTO | null;
	displayablePdpTypes!: TypeDocument[];
}

class DisplayedOperation {
	entity!: AccesOperationTLR;
	label!: string;
	etat!: string;
	acte!: string;
	ouvrage!: string;
}

class DisplayedIndicateur {
	nom!: string;
	icon!: string;
	couleur!: string;
	niveau!: string;
	regle!: string | null;
	dateModification!: LocalDateTime | null | undefined;
}

@Component({
	selector: 'acces-details',
	templateUrl: 'acces-details.html',
	styleUrls: ['acces-details.css'],
})
export class AccesDetailsComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	interAssocieesChapoDisplay = false;
	woAssocieesChapoDisplay = false;
	operationsAssocieeDisplay: boolean[] = [];
	disAssocieeDisplay: boolean[] = [];
	wosAssocieeDisplay: boolean[] = [];
	notifsAssocieeDisplay: boolean[] = [];
	arretsAssocieesChapoDisplay = false;
	commentaireArretDisplay = false;
	arretAssocieDisplay: boolean[] = [];
	niveauAlertIncendie: NiveauAlerteIncendie | null = null;

	// Donnees a lier sur la vue
	loaded = false;
	libellesEtats: string[] = ['Planifié', 'Autorisé', 'En cours', 'Terminé', 'Refusé', 'Conflit', 'Annulé', 'Erreur'];
	libellesTypesAcces: string[] = ['Local', 'A distance'];
	user!: UserTLR;
	acces!: AccesTLR;
	motifRefus!: string;
	initiateur!: string;
	valideur!: string;
	cac!: string;
	cacFermeture!: string;
	participants!: DisplayedParticipant[];
	parc!: ParcTLR;
	antenne!: AntenneTLR;
	energyIcon!: string;
	responsableParc: IntervenantTLR | null = null;
	ouvragesLabel: string = '';
	ouvragesIds: number[] = [];
	ouvragesConcernes!: string;
	etatsOuvrages!: string;
	operations!: DisplayedOperation[];
	demandeInterventions!: DemandeIntervention[];
	workorders!: WorkOrder[];
	notifications!: Notification[];
	arrets!: ArretSlbTLR[];
	canNotify!: boolean;
	memos: Memo[] = [];
	memoOuvrage: Memo[] = [];
	memoOuvrageConcernes: Memo[] = [];
	indicateurs: DisplayedIndicateur[] = [];
	
	displayedMemosParc: DisplayedMemo[] = [];
	
	constructor(
		@Inject(AccesService) accesService: AccesService,
		@Inject(ActeOnMService) acteOnMService: ActeOnMService,
		@Inject(ArretSlbService) arretSlbService: ArretSlbService,
		@Inject(ConstructeurService) constructeurService: ConstructeurService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(ModeleOuvrageService) modeleOuvrageService: ModeleOuvrageService,
		@Inject(MotifRefusService) motifRefusService: MotifRefusService,
		@Inject(OuvrageService) ouvrageService: OuvrageService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(UserService) userService: UserService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(MemoService) private memoService: MemoService,
	) {
		// Petites fonctions d'aides utiles plus loin
		let nullSafeGet = <T>(
			id: number | null | undefined,
			getter: (id: number) => Observable<T>
		) => {
			return of(id).pipe(
				mergeMap(
					(id) => {
						if (id === null || id === undefined) {
							return of(null);
						}

						return getter(id);
					}
				)
			);

		};

		let nullSafeLabelGet = <T>(
			id: number | null | undefined,
			getter: (id: number) => Observable<T>,
			labelGetter: (entity: T) => string,
		) => {
			let entity$: Observable<T | null> = nullSafeGet(id, getter);
			return entity$.pipe(
				map(
					(entity) => entity === null ? '<aucun>' : labelGetter(entity)
				)
			);
		};
		
		// Fonction qui contourne le probleme que les forkJoin de [] ne se resolvent jamais
		let safeForkJoin = <T>(obs: Observable<T>[]) => {
			return obs.length ? forkJoin(obs) : of([]);	
		}

		let intervenantFullName = (intervenant: IntervenantAccesDTO) => {
			if (intervenant.nom === 'CHRG_ACCES_CONDUITE_GENERIQUE') {
				return 'Conduite à distance';
			}

			let nomPrenom = '';
			if (intervenant.prenom != null) {
				nomPrenom = intervenant.prenom + ' ';
			}
			if (intervenant.nom != null) {
				nomPrenom += intervenant.nom;
			}
			return nomPrenom;
		};

		let ouvragesLabel = (ouvrages: OuvrageTLR[]) => ouvrages.map(
			(ouvrage) => {
				let label = ouvrage.nom;
				if (ouvrage.numeroSerie) {
					label += " (" + ouvrage.numeroSerie + ")";
				}

				return label;
			}
		).join(", ")
			;

		// Des le chargement du composant 
		// 1- Charger l'utilisateur courrant
		let user$ = userService.getCurrentUserTLR();

		// 2- Charger les entreprises pour l'acces a leurs noms
		let entreprisesById$ = entrepriseService.getActiveEntreprisesTLRMapById().pipe(
			map(
				(entreprisesMap) => new MandatoryMap(entreprisesMap, "Entreprise")
			)
		);

		// 3- Charger les actes pour acces a leurs noms
		let actesById$ = acteOnMService.getMapById().pipe(
			map(
				(actesMap) => new MandatoryMap(actesMap, "ActeOm")
			)
		);
		
		// 4- Charger les roles possibles pour les intervenants
		let rolesIntervenants$ = staticTypeService.getIntervenantRoleList();
		let rolesIntervenantsById$ = staticTypeService.getIntervenantRoleMapById().pipe(
			map(
				(rolesMap) => new MandatoryMap(rolesMap, "IntervenantRole")
			)
		);
		
		// Chargement des niveaux d'alerte incendie
		let niveauxAlerteIncendie$ = staticTypeService.getNiveauAlerteIncendieMapById().pipe(
			map(
				(values) => new MandatoryMap<number, NiveauAlerteIncendie>(values, "NiveauAlerteIncendie")
			)
		);

		// A la reception d'une demande d'affichage
		// Charger l'acces correspondant
		let acces$ = this.displayCommandsSubject.pipe(
			mergeMap(
				(accesId) => accesService.getById(accesId)
			)
		);

		// Une fois l'acces obtenu, charger toutes les donnees complementaires
		let accesData$ = combineLatest([acces$, rolesIntervenants$, rolesIntervenantsById$]).pipe(
			mergeMap(
				([acces, rolesIntervenants, rolesIntervenantsById]) => {
					let motifRefus$ = nullSafeLabelGet(acces.motifRefusId, motifRefusService.getById, (motif) => motif.libelle);
					let initiateur$ = userService.getUserTLRById(acces.initiateurId).pipe(
						map(
							(initiateur) => initiateur.lastName
						)
					);
					let valideur$ = nullSafeLabelGet(acces.valideurId, userService.getUserTLRById, (user) => user.lastName);
					let cac$ = nullSafeLabelGet(acces.cacId, userService.getUserTLRById, (user) => user.lastName);
					let cacFermeture$ = nullSafeLabelGet(acces.cacIdFermeture, userService.getUserTLRById, (user) => user.lastName);

					let intervenants$ = accesService.getIntervenantsForAcces(acces.id);
					let entreprises$ = acces.etat < 2 ? accesService.getEntreprisesForAcces(acces.id) : of([]);
					let participants$ = combineLatest([intervenants$, entreprises$, entreprisesById$]).pipe(
						map(
							([intervenants, entreprises, entreprisesById]) => {
								// Evol 57237: Il faut presenter les intervenants dans l'ordre imposes par les roles
								let participants = [] as DisplayedParticipant[];
								for (let role of rolesIntervenants) {
									let roleId = role.id;
									let roleLabel = rolesIntervenantsById.get(roleId).label;

									let intervenantsForRole = intervenants.filter($ => $.roleIntervenant === roleId);
									intervenantsForRole.forEach($ => {
										let participant = {
											roleLabel: roleLabel,
											nomEntreprise: $.entrepriseId? entreprisesById.get($.entrepriseId).nom : '',
											nomIntervenant: intervenantFullName($),
											intervenant: $,
											documentApplicable: $.entrepriseId? entreprisesById.get($.entrepriseId).documentApplicable : ''
										} as DisplayedParticipant;
										participants.push(participant);
									});

									let entreprisesForRole = entreprises.filter($ => $.roleEntreprise === roleId);
									entreprisesForRole.forEach($ => {
										let participant = {
											roleLabel: roleLabel,
											nomEntreprise: $.nom,
											nomIntervenant: null,
											intervenant: null,
											documentApplicable: ''
										} as DisplayedParticipant;
										participants.push(participant);
									});
								}

								return participants;
							}
						)
					)

					let parc$ = parcService.getParcTLRById(acces.parcId);
					
					let niveauAlertIncendie$ = combineLatest([ niveauxAlerteIncendie$, parc$ ]).pipe(
					    map(([niveaux, parc]) => {
					        if (!parc || parc.niveauIncendie == null) {
					            return null;
					        }

					        return niveaux.get(parc.niveauIncendie);
					    })
					);

					let energyIcon$ = parc$.pipe(
						map(
							(parc) => parcService.getEnergieAsIcon(parc)
						)
					);

					let responsableParc$ = parc$.pipe(
						mergeMap(
							(parc) => parc.responsableId ? intervenantService.getIntervenantTLRById(parc.responsableId) : of(null)
						)
					);
					
					let indicateurs$ = parc$.pipe(
						mergeMap(
							(parc) => parcService.getParcIndicateurs(parc.id)
						),
						map(
							(indicateurs) => indicateurs
								.filter($ => $.actif !== false)
								.map(
									($) => {
										let config = applicationConstants.indicateurs[$.indicateurCode];
										return {
											nom: config ? config.nom : $.indicateurNom,
											icon: config ? config.icon : 'fa-solid fa-circle-question',
											couleur: couleurIndicateurColors[$.couleur] ?? '#b0b0b0',
											niveau: $.couleur,
											regle: $.regle,
											dateModification: $.dateModification
										} as DisplayedIndicateur;
									}
								)
						)
					);
					
					let antenne$ = parc$.pipe(
						mergeMap(
							(parc) => antenneService.getAntenneTLRById(parc.antenneId)
						)
					);
					

					let memoParc$ = parc$.pipe(
					  mergeMap(parc =>
					    this.memoService.getMemosOnParcForDate(parc.id, acces.dateDebut.toString())
					  )
					);

                    let ouvragesConcernes$ = ouvrageService.getOuvrageConcerneTLRByAcces(acces.id).pipe(
                        map(
                            (ouvrages) => ouvrages.map($ => $.nom).join(", ")
                        )
                    );

					let ouvragesConcerneIds$ = ouvrageService.getOuvrageConcerneTLRByAcces(acces.id).pipe(
					    map(
					        (ouvrages) => ouvrages.map($ => $.id)
					    )
					);
					
					let ouvragesLabel$: Observable<string>;
					let etatsOuvrages$: Observable<string>;
					let ouvragesIds$: Observable<number[]>;
					if (acces.etat === 3) {
						let ouvrages$ = accesService.getOuvrageDTOByAcces(acces.id);
						ouvragesLabel$ = ouvrages$.pipe(
							map(ouvragesLabel)
						);
						etatsOuvrages$ = ouvrages$.pipe(
							map(
								(ouvrages) => ouvrages.map(
									(ouvrage) => ouvrage.nom + " : " + ouvrage.etatAcces
								).join(", ")
							)
						);
						ouvragesIds$ = ouvrages$.pipe(
							map(
								(ouvrages) => ouvrages ? ouvrages.map($ => $.id) : [] as number[]
							)
						);
					} else {
						let ouvrages$ = ouvrageService.getOuvrageTLRByAcces(acces.id);
						ouvragesLabel$ = ouvrages$.pipe(
							map(ouvragesLabel)
						);
						etatsOuvrages$ = of("");
						ouvragesIds$ = ouvrages$.pipe(
							map(
								(ouvrages) => ouvrages ? ouvrages.map($ => $.id) : [] as number[]
							)
						);
					}
					
					let memoOuvrage$ = ouvragesIds$.pipe(
						mergeMap(ouvrages =>
							this.memoService.getMemosOnOuvragesForDate(ouvrages.join(','), acces.dateDebut.toString())
						)
					);

					let memoOuvrageConcernes$ = ouvragesConcerneIds$.pipe(
						mergeMap(ouvragesConcernes =>
							this.memoService.getMemosOnOuvragesForDate(ouvragesConcernes.join(','), acces.dateDebut.toString())
						)
					);
					
					let memoCategories$ = staticTypeService.getMemoCategoryMapById().pipe(
					  map((mcMap) => new MandatoryMap(mcMap, "MemoCategory"))
					);
					let memoSubCategories$ = staticTypeService.getMemoSubCategoryMapById().pipe(
					  map((mcMap) => new MandatoryMap(mcMap, "MemoSubCategory"))
					);
					
					combineLatest([memoParc$, memoCategories$, memoSubCategories$]).subscribe((data) => {
					  let [memos, memoCategories, memoSubCategories] = data;

					  let sortedMemos = memos.sort((a, b) => a.niveauId - b.niveauId);

					  this.displayedMemosParc = sortedMemos.map((memo) => {
					    let categorie = '';

					    if (memo.categorieId) {
					      categorie = memoCategories.get(memo.categorieId).label;
					      if (memo.sousCategorieId) {
					        categorie = categorie + ' (' + memoSubCategories.get(memo.sousCategorieId).label + ') ';
					      }
					      categorie += ' - ';
					    }

					    return {
					      entity: memo,
					      categorie: categorie,
					    } as DisplayedMemo;
					  });
					});

					let demandeInterventions$ = accesService.getDemandeInterventionsByAcces(acces.id);
					let operations$ = accesService.getOperationsTLRByAcces(acces.id);
					let workorders$ = accesService.getAssociatedWorkOrders(acces.id);
					let notifications$ = accesService.getAssociatedNotifications(acces.id);
					let displayedOperations$ = combineLatest([operations$, actesById$]).pipe(
						// Recuperer les donnees complementaires de l'operation a partir du serveur
						mergeMap(
							(data) => {
								let operations = data[0];
								let actesById = data[1];

								return safeForkJoin(
									operations.map(
										(operation) => {
											let ouvrage$ = ouvrageService.getOuvrageTLRById(operation.ouvrageId);

											let modeleOuvrage$ = ouvrage$.pipe(
												mergeMap(
													(ouvrage) => nullSafeGet(ouvrage.modeleOuvrageId, modeleOuvrageService.getById)
												)
											);

											let constructeur$ = modeleOuvrage$.pipe(
												mergeMap(
													(modeleOuvrage) => {
														if (!modeleOuvrage) {
															return of(null);
														}

														return nullSafeGet(modeleOuvrage.constructeurId, constructeurService.getById)
													}
												)
											);

											let actesChain: ActeOnMTLR[] = [];
											let nextActeId = operation.acteOnmId;
											while (nextActeId != null && nextActeId != undefined) {
												let acte = actesById.get(nextActeId);
												actesChain.push(acte);
												nextActeId = acte.parentId;
											}
											actesChain = actesChain.reverse();

											return forkJoin({
												operation: of(operation),
												ouvrage: ouvrage$,
												modeleOuvrage: modeleOuvrage$,
												constructeur: constructeur$,
												actesChain: of(actesChain),
											})
										}
									)
								)
							}
						),
						// Une fois tous les chargement de la base faits, realiser les pre-calculs pour les futurs bindings de la vue
						map(
							(operationsDatas) => operationsDatas.map(
								(loadedData) => {
									let operation = loadedData.operation;
									let ouvrage = loadedData.ouvrage;
									let modeleOuvrage = loadedData.modeleOuvrage;
									let constructeur = loadedData.constructeur;
									let actesChain = loadedData.actesChain;

									let acteLabel = actesChain
										.map(acte => acte.parentId ? acte.libelle : acte.code)
										.join(" - ")
									;

									let operationLabel = (operation.codeCarl != null ? operation.codeCarl : '') 
										+ ' : ' + ouvrage.nom 
										+ ':' + acteLabel
									;

									let etatLabel = '';
									switch (operation.etat) {
										case 'AWAITINGREAL':
											etatLabel = 'Planifié';
											break;
										case 'INPROGRESS':
											etatLabel = 'En cours';
											break;
										default:
											etatLabel = operation.etat;
									}

									let ouvrageLabel = ouvrage.nom;
									if (modeleOuvrage) {
										let elems: string[] = [];
										if (constructeur) {
											elems.push(constructeur.nom);
										}
										if (modeleOuvrage.nom) {
											elems.push(modeleOuvrage.nom);
										}
										if (elems.length > 0) {
											ouvrageLabel += " (" + elems.join(" ") + ")";
										}
									}

									return {
										entity: operation,
										label: operationLabel,
										etat: etatLabel,
										acte: acteLabel,
										ouvrage: ouvrageLabel,
									} as DisplayedOperation;
								}
							)
						)
					);

					// NB: Les arrets SLB ont eux-meme des donnnees complementaires a charger
					let arrets$ = arretSlbService.getByAcces(acces.id);
					
					/* DEBUG ONLY: si la fenetre ne s'ouvre pas sur un click, on peut activer se code pour que la console montre ce qui a pu se resoudre ou pas.
					/* Il suffit d'un seul element manquant pour que l'affichage n'aie pas lieu
					type ObsToCheck = {
					    [key: string]: Observable<any>;
					}
					let toCheck: ObsToCheck  = {
						acces: of(acces), //ok
						motifRefus: motifRefus$,
						initiateur: initiateur$,
						valideur: valideur$,
						cac: cac$,
						cacFermeture: cacFermeture$,
						participants: participants$,
						parc: parc$,
						energyIcon: energyIcon$,
						responsableParc: responsableParc$,
						ouvrages: ouvragesLabel$,
						refOuvrageId; refOuvrageId$,
						ouvragesConcernes: ouvragesConcernes$,
						etatsOuvrages: etatsOuvrages$,
						operations: displayedOperations$,
						arrets: arrets$,
					}
					for (let key in toCheck) {
						let obs = toCheck[key];
						obs.subscribe(
							(_) => console.log("" + key + " resolved")
						)
					}
					*/					

					return forkJoin({
						acces: of(acces),
						motifRefus: motifRefus$,
						initiateur: initiateur$,
						valideur: valideur$,
						cac: cac$,
						cacFermeture: cacFermeture$,
						participants: participants$,
						parc: parc$,
						niveauAlertIncendie: niveauAlertIncendie$,
						energyIcon: energyIcon$,
						responsableParc: responsableParc$,
						ouvragesLabel: ouvragesLabel$,
						ouvragesIds: ouvragesIds$,
						ouvragesConcernes: ouvragesConcernes$,
						etatsOuvrages: etatsOuvrages$,
						operations: displayedOperations$,
						demandeInterventions: demandeInterventions$,
						workorders: workorders$,
						notifications: notifications$,
						arrets: arrets$,
						antenne: antenne$,
						memos: memoParc$,
						memoOuvrage: memoOuvrage$,
						memoOuvrageConcernes: memoOuvrageConcernes$,
						indicateurs: indicateurs$
					});
				}
			)
		);
		
		// Uniquement quand tous les elements sont charges, on bind les donnees sur les vue
		combineLatest([user$, accesData$]).subscribe(
			(data) => {
				this.user = data[0]
				let accesData = data[1];
				this.acces = accesData.acces;
				this.motifRefus = accesData.motifRefus;
				this.initiateur = accesData.initiateur;
				this.valideur = accesData.valideur;
				this.cac = accesData.cac;
				this.cacFermeture = accesData.cacFermeture;
				this.participants = accesData.participants;
				this.participants.forEach( participant => {
					if (this.acces.typeAcces  === 1) {
						participant.displayablePdpTypes = [];
					} else {
						if (participant.nomEntreprise && participant.nomEntreprise.toUpperCase().startsWith('ENGIE GREEN')) {
							participant.displayablePdpTypes = [TypeDocument.SPECIFIQUE];
						} else {
							if (participant.documentApplicable === 'Both') {
								participant.displayablePdpTypes = [TypeDocument.GENERAL, TypeDocument.SPECIFIQUE, TypeDocument.PA];
							} else if (participant.documentApplicable === 'PersonneAvertie') {
								participant.displayablePdpTypes = [TypeDocument.PA];
							} else if (participant.documentApplicable === 'PlanPrevention') {
								participant.displayablePdpTypes = [TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
							}
						}
					}
				} );
				this.parc = accesData.parc;
				this.niveauAlertIncendie = accesData.niveauAlertIncendie;
				this.antenne = accesData.antenne;
				this.energyIcon = accesData.energyIcon;
				this.responsableParc = accesData.responsableParc;
				this.ouvragesLabel = accesData.ouvragesLabel;
				this.ouvragesIds = accesData.ouvragesIds;
				this.ouvragesConcernes = accesData.ouvragesConcernes;
				this.etatsOuvrages = accesData.etatsOuvrages;
				this.operations = accesData.operations;
				this.demandeInterventions = accesData.demandeInterventions;
				this.workorders = accesData.workorders;
				this.notifications = accesData.notifications;
				this.arrets = accesData.arrets;
				this.canNotify = this.acces.initiateurId === this.user.id || this.acces.cacId === this.user.id;

				this.loaded = true;
				this.displaySubject.next(true);
				this.memos = accesData.memos;
				this.memoOuvrage = accesData.memoOuvrage;
				this.memoOuvrageConcernes = accesData.memoOuvrageConcernes;
				this.indicateurs = accesData.indicateurs;
			}
		)

		// Quand on masque la fenetre de dialogue, on nettoie les donnees passees
		this.displaySubject.pipe(
			filter(show => !show)
		).subscribe(
			(_show) => {
				this.loaded = false;
			}
		)
	}

	@Input() id = 'accesDetailsPopup';

	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommandsSubject = new SubjectRelay<number>();
	@Input() set displayCommands(obs: Observable<number> | undefined) {
		this.displayCommandsSubject.registerSource(obs);
	}
	
	@Output() notificationRequested = new EventEmitter<AccesTLR>();

	notifier() {
		this.displaySubject.next(false);
		this.notificationRequested.emit(this.acces);
	};
	
	close() {
		this.displaySubject.next(false);
	}
}